import type {ChevronProps, DayPicker} from "@daypicker/react";
import {ChevronLeft, ChevronRight} from "lucide-react";
import type * as React from "react";
import { Button as NewButton } from "@/components/ui/NewButton";
import {cn} from "@/lib/utils";

// Common components/functions for date pickers.

export const monthStart = (date: Date) => {
  return new Date(date.getFullYear(), date.getMonth(), 1);
};

export const Chevron = ({ orientation, className, ...props }: ChevronProps) => {
  return orientation === "left" ? (
    <ChevronLeft className={cn("h-4 w-4", className)} aria-hidden="true" {...props} />
  ) : (
    <ChevronRight className={cn("h-4 w-4", className)} aria-hidden="true" {...props} />
  );
};

type TopControlsProps = {
  enabled: boolean;
  month: Date;
  onChange: (date: Date) => void;
  /** Button style for the Prev/Today/Next controls. Defaults to "outline". */
  variant?: "outline" | "ghost";
};

export const TopControls = ({enabled, month, onChange, variant = "outline"}: TopControlsProps) => {
  if (!enabled) {
    return null;
  }
  // Ghost drops the border for a lighter look; outline keeps the bordered chip.
  const buttonClassName =
    variant === "ghost"
      ? "text-gray-700 hover:bg-gray-50"
      : "border-gray-200 text-gray-700 hover:bg-gray-50";
  return (
    <div className="flex items-center justify-between gap-2 border-b border-gray-200 px-3 py-2">
      <NewButton
        type="button"
        variant={variant}
        size="xs"
        onClick={() => onChange(new Date(month.getFullYear() - 1, month.getMonth(), 1))}
        className={buttonClassName}
      >
        Previous Year
      </NewButton>
      <NewButton
        type="button"
        variant={variant}
        size="xs"
        onClick={() => onChange(monthStart(new Date()))}
        className={buttonClassName}
      >
        Today
      </NewButton>
      <NewButton
        type="button"
        variant={variant}
        size="xs"
        onClick={() => onChange(new Date(month.getFullYear() + 1, month.getMonth(), 1))}
        className={buttonClassName}
      >
        Next Year
      </NewButton>
    </div>
  );
};

export type DayPickerClassNames = NonNullable<
  React.ComponentProps<typeof DayPicker>["classNames"]
>;

export const dayPickerBaseClassNames: Partial<DayPickerClassNames> = {
  months: "relative flex flex-col gap-4 sm:flex-row",
  month: "space-y-4",
  month_caption: "relative flex items-center justify-center pt-1",
  caption_label: "text-sm font-medium text-gray-900",
  nav: "absolute inset-x-0 top-1 z-10 flex items-center justify-between px-1",
  button_previous:
    "inline-flex h-7 w-7 items-center justify-center rounded-md border border-gray-200 bg-white p-0 text-gray-600 hover:bg-gray-50",
  button_next:
    "inline-flex h-7 w-7 items-center justify-center rounded-md border border-gray-200 bg-white p-0 text-gray-600 hover:bg-gray-50",
  month_grid: "w-full border-collapse space-y-1",
  weekdays: "flex",
  weekday: "text-muted-foreground w-8 rounded-md text-[0.8rem] font-normal",
  week: "mt-2 flex w-full",
  today: "[&>button]:font-medium",
  disabled: "[&>button]:cursor-not-allowed [&>button]:text-muted-foreground [&>button]:opacity-50",
  hidden: "invisible",
};
